import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * An empty state should read as a deliberate resting state, not a failed load.
 * Left-aligned inside a hairline frame, with the icon at label weight.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start gap-4 px-4 py-10 sm:px-6', className)}>
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line bg-sunken text-fg-muted">
        <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-base font-medium text-fg">{title}</p>
        {description ? (
          <p className="mt-1 max-w-prose text-sm leading-normal text-fg-secondary">{description}</p>
        ) : null}
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    </div>
  );
}

/** Non-blocking error surface used inside panels when a query fails. */
export function InlineError({
  message,
  onRetry,
  className,
}: {
  message: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-md border border-danger-500 bg-danger-50 px-3 py-2.5',
        className,
      )}
    >
      {/* `danger-500` rather than `danger-700`: the 700 step has no dark-mode
          override, so on the dark `danger-50` plate it drops to about 1.9:1.
          The 500 step clears 4.5:1 in both themes. */}
      <p className="text-sm text-danger-500">{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="focus-ring tap-target shrink-0 rounded-sm text-sm font-medium text-danger-500 underline underline-offset-2"
        >
          Повторить
        </button>
      ) : null}
    </div>
  );
}
